/**
 * Octopus IoT Platform - Alert Center Page
 * B1: Central page for viewing and managing all alerts
 * 
 * Features:
 * - Filterable table with all alerts
 * - Detail panel on click
 * - Socket.IO real-time updates
 * - Acknowledge/Resolve actions
 * - Geofence mini-map for geofence alerts
 * 
 * Reference: docs/OCTOPUS_ALERTCENTER_DESIGN_DOC.md
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Bell,
  Settings,
  WifiOff,
  TrendingUp,
  Hexagon,
  CloudSun,
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCheck,
  CheckCircle2,
  RefreshCw,
  ExternalLink,
  Clock,
  Loader2,
  Filter,
  X,
  MoreVertical,
  Cog,
  ArrowLeftRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { severityConfig } from '@/lib/alertSeverity';
import alertsApi from '@/services/alertsApi';
import NotificationSettingsModal from '@/components/alerts/NotificationSettingsModal';
import { useTimezone } from '@/contexts/TimezoneContext';

const SOCKET_PATH = '/api/socket.io';

// Severity configuration — fonte única em @/lib/alertSeverity (compartilhada com os
// badges do "Ao vivo", D-01 zero drift de paleta).

// Alert type icons
const typeIcons = {
  offline: WifiOff,
  threshold: TrendingUp,
  geofence: Hexagon,
  weather_route: CloudSun,
  source_detected: Settings,
  engine_notification: Cog,
  gearbox_abuse: ArrowLeftRight,
};

// Rótulos PT-BR dos tipos de alerta (UI-REVIEW achado 5 — enum cru nunca vaza na UI)
const typeLabels = {
  offline: 'Offline',
  threshold: 'Limite',
  geofence: 'Geofence',
  weather_route: 'Previsão em Rota',
  source_detected: 'Nova Fonte',
  system_health: 'Saúde do Sistema',
  engine_notification: 'Painel do motor',
  gearbox_abuse: 'Reversão brusca da gearbox',
};

// Mapa alert_type → categoria da página do equipamento (Fase 1000, AD-04):
// alimenta o deep-link "Configurar este alerta" -> `/equipments/{id}/alerts?cat=`.
// stream_blindness/gearbox_abuse vivem dentro de outras seções da página nova
// (connectivity/engine_notification) — ver EquipmentAlertsPage.jsx CAT_SECTION_MAP.
const categoryFromAlertType = (alertType) => ({
  engine_notification: 'engine_notification',
  stream_blindness: 'stream_blindness',
  gearbox_abuse: 'gearbox_abuse',
  fuel_reserve: 'fuel',
  offline: 'connectivity',
  threshold: 'telemetry',
  weather_route: 'weather',
  geofence: 'geofence',
}[alertType] || alertType);

// Detecção de desktop por largura pura em 1024px (lg) — REDESIGN-SPEC §5.2 regra 2.
// NÃO reusar useIsMobile.js (mistura breakpoint 768 + coarse-pointer/iOS): aqui o corte
// que importa é exatamente o breakpoint lg do Tailwind.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return isDesktop;
};

// Corpo único de detalhes do alerta — renderizado no Card lateral (lg+) OU no Sheet (<lg).
// Zero duplicação de JSX: toda a lógica/copy selada de Ciente/Encerrar vive só aqui.
const AlertDetailBody = ({
  alert,
  actionLoading,
  handleAcknowledge,
  handleResolve,
  handleConfigureSource,
  handleIgnoreSource,
  handleConfigureAlert,
  formatFullDate,
  formatDateTime,
  typeLabels: labels
}) => {
  // D-20 (Phase 17.1 Plan 09): alerta critical que auto-resolveu SEM ciência prévia —
  // continua acionável (Ciente) mesmo com status='resolved'; consome a flag do
  // backend, nunca recomputa (INV-UI-TRAIL-001).
  const isUnackedCritical = alert.status === 'resolved' && alert.resolved_without_ack && !alert.acknowledged_at;
  return (
  <div className="p-4 space-y-4">
    {/* Alert Header */}
    <div className={cn(
      'p-4 rounded-lg border-l-4',
      severityConfig[alert.severity]?.bg,
      severityConfig[alert.severity]?.border
    )}>
      <div className="flex items-center gap-2 mb-2">
        {(() => {
          const Icon = typeIcons[alert.alert_type] || AlertCircle;
          return <Icon className={cn('w-5 h-5', severityConfig[alert.severity]?.color)} />;
        })()}
        <span className="font-semibold">{alert.equipment_name}</span>
      </div>
      <p className="text-sm">{alert.message}</p>
    </div>

    <Separator />

    {/* Details */}
    <div className="space-y-3">
      <DetailRow label="Tipo" value={labels[alert.alert_type] || alert.alert_type} />
      <DetailRow label="Severidade" value={severityConfig[alert.severity]?.label || alert.severity} />
      <DetailRow label="Status" value={
        alert.status === 'active' ? 'Ativo' :
        alert.status === 'acknowledged' ? 'Ciente' :
        isUnackedCritical ? 'Encerrado sem ciência' : 'Encerrado'
      } />
      <DetailRow label="Disparado em" value={formatFullDate(alert.triggered_at)} />
      {alert.acknowledged_at && (
        <DetailRow label="Ciente em" value={formatFullDate(alert.acknowledged_at)} />
      )}
      {alert.resolved_at && (
        <DetailRow label="Encerrado em" value={formatFullDate(alert.resolved_at)} />
      )}
      {/* D-20: latch de visibilidade — alarme critical que auto-resolveu antes de
          alguém dar ciência continua destacado até a ciência ser dada. */}
      {isUnackedCritical && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/30 text-destructive">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="text-xs font-medium">
            Encerrado sem ciência — este alarme crítico foi automaticamente encerrado antes de alguém dar ciência. Dê ciência para removê-lo desta lista.
          </span>
        </div>
      )}
      {/* Janela D-05: leitura histórica do alarme do painel do motor nos Encerrados —
          "ativo entre {início} e {fim}". Reusa o formatador PT-BR (omitSeconds),
          nunca new Date().toLocaleString() manual (INV-UI-TRAIL-001). */}
      {alert.status === 'resolved' && alert.alert_type === 'engine_notification' && (
        <p className="text-xs text-muted-foreground">
          ativo entre {formatDateTime(alert.triggered_at, { omitSeconds: true })} e {formatDateTime(alert.resolved_at, { omitSeconds: true })}
        </p>
      )}
      {alert.current_value !== null && alert.current_value !== undefined && (
        <DetailRow label="Valor atual" value={alert.current_value} />
      )}
      {alert.threshold_value !== null && alert.threshold_value !== undefined && (
        <DetailRow label="Limite configurado" value={alert.threshold_value} />
      )}
    </div>

    <Separator />

    {/* Actions */}
    {alert.alert_type === 'source_detected' && alert.status === 'active' && (
      <div className="flex flex-col gap-2">
        <Button
          onClick={() => handleConfigureSource(alert)}
          disabled={actionLoading}
          data-testid="detail-configure-source-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          {alert.action_label || 'Configurar fonte'}
        </Button>
        <Button
          variant="outline"
          onClick={() => handleIgnoreSource(alert)}
          disabled={actionLoading}
          data-testid="detail-ignore-source-btn"
        >
          {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
          Ignorar fonte
        </Button>
      </div>
    )}

    {(alert.status !== 'resolved' || isUnackedCritical) && alert.alert_type !== 'source_detected' && (
      <TooltipProvider>
        <div className="flex flex-col gap-2">
          {(alert.status === 'active' || isUnackedCritical) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => handleAcknowledge(alert.id)}
                  disabled={actionLoading}
                  data-testid="detail-acknowledge-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Ciente
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isUnackedCritical
                  ? 'Dar ciência remove este alerta encerrado da visão padrão (o ciclo de vida não muda, ele continua Encerrado).'
                  : 'Não volta enquanto durar — some da lista até a condição cessar (o sistema encerra sozinho quando cessa).'}
              </TooltipContent>
            </Tooltip>
          )}
          {alert.status !== 'resolved' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => handleResolve(alert.id)}
                  disabled={actionLoading}
                  data-testid="detail-resolve-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
                  Encerrar
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Fecha agora. Se a condição persistir ou voltar, um novo alerta é criado.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    )}

    {/* "Configurar este alerta" (Fase 1000, AD-04): a Central virou acompanhamento —
        este é o atalho para a superfície de edição real (página do equipamento).
        Oculto para alertas de saúde de sistema (equipment_id="__system__", sem
        contexto de equipamento para o deep-link). */}
    {alert.equipment_id !== '__system__' && (
      <Button
        variant="link"
        className="w-full justify-start px-0 text-primary"
        onClick={() => handleConfigureAlert?.(alert)}
        data-testid="configure-this-alert-link"
      >
        <Settings className="w-4 h-4 mr-2" />
        Configurar este alerta
      </Button>
    )}

    {/* Link to Equipment — oculto para alertas de saúde de sistema (D-09):
        equipment_id="__system__" seria um link morto */}
    {alert.alert_type !== 'system_health' && alert.equipment_id !== '__system__' && (
      <Link to={`/equipments/${alert.equipment_id}/live`}>
        <Button variant="link" className="w-full justify-start px-0" data-testid="view-equipment-link">
          <ExternalLink className="w-4 h-4 mr-2" />
          Ver equipamento
        </Button>
      </Link>
    )}

    {/* Geofence Mini-map placeholder */}
    {alert.alert_type === 'geofence' && (
      <div className="mt-4">
        <p className="text-sm font-medium mb-2">Localização</p>
        <div className="h-40 bg-muted rounded-lg flex items-center justify-center">
          <Hexagon className="w-8 h-8 text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground mt-1 text-center">
          Mini-mapa disponível na página do equipamento
        </p>
      </div>
    )}
  </div>
  );
};

const AlertsPage = () => {
  const navigate = useNavigate();
  const { formatDateTime, formatTime } = useTimezone();

  // State
  const [alerts, setAlerts] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const isDesktop = useIsDesktop();
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Ao cruzar para desktop com o sheet aberto, fecha — o card lateral já mostra o mesmo selectedAlert.
  useEffect(() => {
    if (isDesktop) setDetailSheetOpen(false);
  }, [isDesktop]);

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterEquipment, setFilterEquipment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('default');
  
  // Pagination
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const params = { limit: 500 };
      // 'default' = Ativos+Cientes (omite status → backend devolve active+acknowledged, corrigido no Plano 01);
      // 'all' envia status='all' (backend trata como todos); status específico envia o valor
      if (filterStatus !== 'default') {
        params.status = filterStatus;
      }
      if (filterEquipment !== 'all') {
        params.equipment_id = filterEquipment;
      }
      
      const response = await alertsApi.getAlerts(params);
      let data = response.data;
      
      // Apply client-side filters for type and severity
      if (filterType !== 'all') {
        data = data.filter(a => a.alert_type === filterType);
      }
      if (filterSeverity !== 'all') {
        data = data.filter(a => a.severity === filterSeverity);
      }
      
      setAlerts(data);
      // Reconcilia o painel Detalhes com o dataset fresco: se o alerta selecionado continua
      // presente, mostra a versão atualizada; se sumiu (ex.: resolvido sob filtro Ativos+Cientes
      // ou por um bulk disparado em outra aba/auto-resolve), volta a "Selecione um alerta".
      // Functional update — não adiciona `selectedAlert` às deps deste useCallback.
      setSelectedAlert(prev => prev ? (data.find(a => a.id === prev.id) ?? null) : null);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Erro ao carregar alertas');
    }
  }, [filterStatus, filterEquipment, filterType, filterSeverity]);

  // Fetch equipments for filter dropdown
  const fetchEquipments = async () => {
    try {
      const response = await alertsApi.getEquipments();
      setEquipments(response.data);
    } catch (error) {
      console.error('Error fetching equipments:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchAlerts(), fetchEquipments()]);
      setLoading(false);
    };
    load();
  }, [fetchAlerts]);

  // Socket.IO real-time updates
  useEffect(() => {
    const socket = io(process.env.REACT_APP_BACKEND_URL, { 
      path: SOCKET_PATH,
      transports: ['websocket', 'polling']
    });

    socket.on('new_alert', (alert) => {
      // Check if alert passes current filters
      const passesFilters = 
        (filterType === 'all' || alert.alert_type === filterType) &&
        (filterSeverity === 'all' || alert.severity === filterSeverity) &&
        (filterEquipment === 'all' || alert.equipment_id === filterEquipment) &&
        (filterStatus === 'all' || filterStatus === 'default' || alert.status === filterStatus);
      
      if (passesFilters) {
        setAlerts(prev => [alert, ...prev]);
      }
      setLastUpdate(new Date());
      toast.info(`Novo alerta: ${alert.equipment_name}`, {
        description: alert.message
      });
    });

    socket.on('alert_updated', (updatedAlert) => {
      setAlerts(prev => 
        prev.map(a => a.id === updatedAlert.id ? updatedAlert : a)
      );
      if (selectedAlert?.id === updatedAlert.id) {
        setSelectedAlert(updatedAlert);
      }
      setLastUpdate(new Date());
    });

    // Payload agregado {action, matched, modified} — sem docs individuais. Cobre bulks
    // disparados por OUTRAS abas/clientes e auto-resolves do backend; fetchAlerts (useCallback
    // estável) já reconcilia selectedAlert internamente.
    socket.on('alert_bulk_updated', () => {
      fetchAlerts();
    });

    return () => {
      socket.disconnect();
    };
  }, [filterType, filterSeverity, filterEquipment, filterStatus, selectedAlert, fetchAlerts]);

  // Re-fetch when filters change
  useEffect(() => {
    fetchAlerts();
    setPage(1);
  }, [filterType, filterSeverity, filterEquipment, filterStatus, fetchAlerts]);

  // Actions
  const handleAcknowledge = async (alertId) => {
    setActionLoading(true);
    try {
      await alertsApi.acknowledgeAlert(alertId);
      toast.success('Alerta marcado como ciente');
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error('Erro ao marcar como ciente');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async (alertId) => {
    setActionLoading(true);
    try {
      await alertsApi.resolveAlert(alertId);
      toast.success('Alerta encerrado');
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error('Erro ao encerrar alerta');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfigureSource = (alert) => {
    if (alert?.action_url) {
      navigate(alert.action_url);
    }
  };

  // "Configurar este alerta" (Fase 1000, AD-04): deep-link para a página do
  // equipamento, na categoria e regra corretas (?cat=/?highlight=).
  const handleConfigureAlert = (alert) => {
    if (!alert?.equipment_id || alert.equipment_id === '__system__') return;
    const cat = categoryFromAlertType(alert.alert_type);
    const highlight = alert.alert_rule_id || '';
    navigate(`/equipments/${alert.equipment_id}/alerts?cat=${cat}&highlight=${encodeURIComponent(highlight)}`);
  };

  const handleIgnoreSource = async (alert) => {
    const sourceGroupId = alert?.metadata?.source_group_id;
    if (!sourceGroupId || !alert?.equipment_id) return;
    setActionLoading(true);
    try {
      await alertsApi.ignoreSourceGroup(alert.equipment_id, sourceGroupId);
      toast.success('Fonte ignorada');
      await fetchAlerts();
    } catch (error) {
      console.error('Error ignoring source group:', error);
      toast.error('Erro ao ignorar fonte');
    } finally {
      setActionLoading(false);
    }
  };

  const buildBulkParams = useCallback(() => {
    const params = {};
    // Diferente do fetchAlerts DE PROPÓSITO: bulk omite status para 'all' E 'default',
    // deixando as rotas bulk aplicarem seus defaults assimétricos (Pitfall 6).
    if (filterStatus !== 'all' && filterStatus !== 'default') params.status = filterStatus;
    if (filterEquipment !== 'all') params.equipment_id = filterEquipment;
    if (filterType !== 'all') params.alert_type = filterType;
    if (filterSeverity !== 'all') params.severity = filterSeverity;
    return params;
  }, [filterStatus, filterEquipment, filterType, filterSeverity]);

  const handleAcknowledgeAll = async () => {
    setActionLoading(true);
    try {
      const params = buildBulkParams();
      // Acknowledge only applies to active alerts.
      if (!params.status || params.status === 'all') params.status = 'active';
      const response = await alertsApi.acknowledgeAllAlerts(params);
      const changed = response?.data?.modified ?? 0;
      toast.success(`${changed} alerta(s) marcado(s) como ciente`);
      await fetchAlerts();
    } catch (error) {
      console.error('Error acknowledging alerts in bulk:', error);
      toast.error('Erro ao marcar alertas como ciente');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveAll = async () => {
    setActionLoading(true);
    try {
      const params = buildBulkParams();
      const response = await alertsApi.resolveAllAlerts(params);
      const changed = response?.data?.modified ?? 0;
      toast.success(`${changed} alerta(s) encerrado(s)`);
      await fetchAlerts();
    } catch (error) {
      console.error('Error resolving alerts in bulk:', error);
      toast.error('Erro ao encerrar alertas');
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterType('all');
    setFilterSeverity('all');
    setFilterEquipment('all');
    setFilterStatus('default');
  };

  // Formatting
  const formatAlertTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return `agora • ${formatDateTime(isoString, { omitSeconds: true })}`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min • ${formatDateTime(isoString, { omitSeconds: true })}`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h • ${formatDateTime(isoString, { omitSeconds: true })}`;
    return formatDateTime(isoString, { omitSeconds: true });
  };

  const formatFullDate = (isoString) => {
    if (!isoString) return '-';
    return formatDateTime(isoString);
  };

  // Pagination
  const paginatedAlerts = alerts.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(alerts.length / ITEMS_PER_PAGE);

  const hasActiveFilters = filterType !== 'all' || filterSeverity !== 'all' || filterEquipment !== 'all' || filterStatus !== 'default';
  const selectedFilterEquipment = equipments.find(eq => eq.id === filterEquipment);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container-fill flex flex-col flex-1 min-h-0 gap-4 md:gap-6 min-w-0" data-testid="alerts-page">
      <h1 className="text-2xl font-bold">Alertas</h1>

      <TooltipProvider>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <Bell className="w-6 h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl md:text-2xl font-bold truncate">Central de Alertas</h2>
            <p className="text-sm text-muted-foreground">
              {alerts.length} alertas
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Atualizar"
                onClick={() => fetchAlerts()}
                data-testid="refresh-alerts-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Atualizar</TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Mais opções"
                data-testid="alerts-toolbar-overflow-btn"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                disabled={!selectedFilterEquipment}
                onSelect={() => navigate(`/equipments/${selectedFilterEquipment.id}/alerts`)}
                data-testid="configure-alert-rules-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                <div className="flex flex-col">
                  <span>Configurar Regras</span>
                  {!selectedFilterEquipment && (
                    <span className="text-xs text-muted-foreground">Selecione um equipamento no filtro</span>
                  )}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShowSettings(true)} data-testid="settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Configurar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6 hidden sm:block" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 whitespace-nowrap"
                onClick={handleAcknowledgeAll}
                disabled={actionLoading}
                data-testid="acknowledge-all-alerts-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Ciente em todos
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Não volta enquanto durar — some da lista até a condição cessar (o sistema encerra sozinho quando cessa).
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="shrink-0 whitespace-nowrap"
                onClick={handleResolveAll}
                disabled={actionLoading}
                data-testid="resolve-all-alerts-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
                Encerrar todos
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Fecha agora. Se a condição persistir ou voltar, um novo alerta é criado.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      </TooltipProvider>

      {/* Filters */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros</span>
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground h-7"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1 min-w-0">
              <label className="text-xs font-semibold text-muted-foreground" htmlFor="filter-type">Tipo</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger id="filter-type" className="w-full min-w-0 [&>span]:truncate" data-testid="filter-type">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos tipos</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="threshold">Threshold</SelectItem>
                  <SelectItem value="geofence">Geofence</SelectItem>
                  <SelectItem value="weather_route">Previsão em Rota</SelectItem>
                  <SelectItem value="source_detected">Nova Fonte</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 min-w-0">
              <label className="text-xs font-semibold text-muted-foreground" htmlFor="filter-severity">Severidade</label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger id="filter-severity" className="w-full min-w-0 [&>span]:truncate" data-testid="filter-severity">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="low">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 min-w-0">
              <label className="text-xs font-semibold text-muted-foreground" htmlFor="filter-equipment">Equipamento</label>
              <Select value={filterEquipment} onValueChange={setFilterEquipment}>
                <SelectTrigger id="filter-equipment" className="w-full min-w-0 [&>span]:truncate" data-testid="filter-equipment">
                  <SelectValue placeholder="Equipamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos equipamentos</SelectItem>
                  {equipments.map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 min-w-0">
              <label className="text-xs font-semibold text-muted-foreground" htmlFor="filter-status">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger id="filter-status" className="w-full min-w-0 [&>span]:truncate" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Ativos + Cientes</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="acknowledged">Cientes</SelectItem>
                  <SelectItem value="resolved">Encerrados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Two Panel Layout
          Auto-fit: a grade estica na altura (flex-1 min-h-0 + grid-rows-1) e os
          cards viram h-full com piso min-h-[360px]; o scroll fica contido na
          lista, nunca na página inteira. Ver RESPONSIVE_STANDARD.md. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 grid-rows-1 gap-4 lg:gap-6 flex-1 min-h-0">
        {/* Alert List */}
        <div className="lg:col-span-2 min-w-0 min-h-0">
          <Card className="h-full min-h-[360px] flex flex-col">
            <CardHeader className="py-3 px-4 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Alertas</CardTitle>
                <span className="text-xs text-muted-foreground">
                  Atualizado: {formatTime(lastUpdate)}
                </span>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              {alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] lg:h-[400px] text-muted-foreground">
                  <Bell className="w-12 h-12 mb-4 opacity-30" />
                  <p>Nenhum alerta encontrado</p>
                  {hasActiveFilters && (
                    <Button variant="link" onClick={clearFilters} className="mt-2">
                      Limpar filtros
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {paginatedAlerts.map((alert) => {
                    const severity = severityConfig[alert.severity] || severityConfig.medium;
                    const TypeIcon = typeIcons[alert.alert_type] || AlertCircle;
                    const isSelected = selectedAlert?.id === alert.id;
                    const isAcknowledged = alert.status === 'acknowledged';
                    const isResolved = alert.status === 'resolved';
                    // D-20 (Plan 09): alerta critical encerrado SEM ciência prévia — nunca
                    // esmaecido (opacity-60), destacado até alguém dar ciência.
                    const isUnackedCritical = isResolved && alert.resolved_without_ack && !alert.acknowledged_at;
                    const iconBg = isAcknowledged ? severity.bg.replace('/10', '/5') : severity.bg;

                    return (
                      <div
                        key={alert.id}
                        className={cn(
                          'p-4 cursor-pointer transition-all hover:bg-muted/50',
                          isSelected && 'bg-primary/5 border-l-2 border-l-primary',
                          isResolved && !isUnackedCritical && 'opacity-60'
                        )}
                        onClick={() => {
                          setSelectedAlert(alert);
                          if (!isDesktop) setDetailSheetOpen(true);
                        }}
                        data-testid={`alert-row-${alert.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn('p-2 rounded-lg', iconBg)}>
                            <TypeIcon className={cn('w-4 h-4', severity.color)} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 min-w-0 flex-wrap">
                              <span className="font-medium truncate min-w-0">{alert.equipment_name}</span>
                              <Badge variant="outline" className={cn('text-xs shrink-0', severity.color)}>
                                {severity.label}
                              </Badge>
                              {isAcknowledged && (
                                <Badge variant="secondary" className="text-xs text-muted-foreground shrink-0">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Ciente
                                </Badge>
                              )}
                              {isUnackedCritical ? (
                                <Badge variant="destructive" className="text-xs shrink-0 gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Encerrado sem ciência
                                </Badge>
                              ) : isResolved && (
                                <Badge variant="secondary" className="text-xs shrink-0">
                                  Encerrado
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{alert.message}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {formatAlertTime(alert.triggered_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                  Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(page * ITEMS_PER_PAGE, alerts.length)} de {alerts.length}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Próximo
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Detail Panel — só existe (montado) em desktop; em <lg vira Sheet abaixo */}
        {isDesktop && (
          <div className="lg:col-span-1 min-w-0 min-h-0">
            <Card className="h-full min-h-[360px] flex flex-col">
              <CardHeader className="py-3 px-4 border-b flex-shrink-0">
                <CardTitle className="text-base">Detalhes</CardTitle>
              </CardHeader>

              {selectedAlert ? (
                <ScrollArea className="flex-1">
                  <AlertDetailBody
                    alert={selectedAlert}
                    actionLoading={actionLoading}
                    handleAcknowledge={handleAcknowledge}
                    handleResolve={handleResolve}
                    handleConfigureSource={handleConfigureSource}
                    handleIgnoreSource={handleIgnoreSource}
                    handleConfigureAlert={handleConfigureAlert}
                    formatFullDate={formatFullDate}
                    formatDateTime={formatDateTime}
                    typeLabels={typeLabels}
                  />
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Bell className="w-12 h-12 mb-4 opacity-30" />
                  <p>Selecione um alerta</p>
                  <p className="text-xs">para ver os detalhes</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Detalhes em Sheet — só existe (montado) em <lg */}
      {!isDesktop && (
        <Sheet open={detailSheetOpen} onOpenChange={(o) => { if (!o) setDetailSheetOpen(false); }}>
          <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="alert-detail-sheet">
            <SheetHeader className="py-3 px-4 border-b">
              <SheetTitle className="text-base">Detalhes</SheetTitle>
            </SheetHeader>
            <ScrollArea className="flex-1">
              {selectedAlert && (
                <AlertDetailBody
                  alert={selectedAlert}
                  actionLoading={actionLoading}
                  handleAcknowledge={handleAcknowledge}
                  handleResolve={handleResolve}
                  handleConfigureSource={handleConfigureSource}
                  handleIgnoreSource={handleIgnoreSource}
                  handleConfigureAlert={handleConfigureAlert}
                  formatFullDate={formatFullDate}
                  formatDateTime={formatDateTime}
                  typeLabels={typeLabels}
                />
              )}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />

    </div>
  );
};

// Helper component for detail rows
const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium">{value}</span>
  </div>
);

export default AlertsPage;
